// API base URL configuration
// import axios from 'axios';

import type {
  AssistantCommunityApiResponse,
  AssistantListPayload,
  AssistantPrivilegesPayload,
  AssistantResponseDto,
  AvailablePrivilegesPayload,
  ChangeAssistantPasswordDto,
  CommentFilterDto,
  CommentStatus,
  PaginatedCommentsResponseDto,
  RegisterAssistantDto,
  UpdateAssistantDto,
  UpdateAssistantPrivilegesDto,
} from '../types/assistantCommunity';
import type {
  CreateWalletCodeCategoryDto,
  CreateWalletCodesDto,
  TransactionListParams,
  UpdateWalletCodeCategoryDto,
  WalletCodeListParams,
} from '../types/wallet';
import type {
  ModeratorApiResponse,
  ModeratorResponseDto,
  RegisterModeratorDto,
  UpdateModeratorDto,
} from '../types/moderator';

export const API_BASE_URL = 'https://api.mromarelkholy.com/api';

// Multi-tenant configuration
export const TENANT_ID = 'tenant6';

// Memory storage for auth data
class AuthService {
  private userData: any = null;
  private refreshPromise: Promise<boolean> | null = null;
  private authChangeListeners: (() => void)[] = [];
  private lastRefreshAttempt: number = 0;
  private readonly minRefreshInterval: number = 5000; // 5 seconds minimum between refresh attempts

  constructor() {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        this.userData = parsedAuth.userData;
      } catch (error) {
        console.error('Failed to parse saved auth data');
        this.clearAuthData();
      }
    }
  }

  setAuthData(token: string, userData: any): void {
    if (!token) {
      console.error('Attempted to set null or undefined token');
      return;
    }
    this.userData = userData;
    const authData = {
      token,
      userData,
      timestamp: Date.now()
    };
    localStorage.setItem('authData', JSON.stringify(authData));
    localStorage.setItem('isAuthenticated', 'true');
    this.notifyAuthChange();
  }

  getToken(): string | null {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.token || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting token');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  getUserData(): any {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        return parsedAuth.userData || null;
      } catch (error) {
        console.error('Failed to parse saved auth data when getting user data');
        this.clearAuthData();
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Check if token is too old (more than 23 hours - giving 1 hour buffer for refresh)
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        const tokenAge = Date.now() - (parsedAuth.timestamp || 0);
        const maxAge = 23 * 60 * 60 * 1000; // 23 hours in milliseconds
        
        if (tokenAge > maxAge) {
          console.log('Token too old, clearing auth data');
          this.clearAuthData();
          return false;
        }
      } catch (error) {
        console.error('Failed to check token age:', error);
        this.clearAuthData();
        return false;
      }
    }
    
    return true;
  }

  async logout(): Promise<void> {
    try {
      console.log('AuthService: Starting logout API call...');
      const role = this.getUserData()?.role;
      const logoutPath = role === 'Assistant' ? 'assistant/logout' : 'ModeratorAuth/logout';
      await fetch(`${API_BASE_URL}/${logoutPath}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': this.getToken() ? `Bearer ${this.getToken()}` : '',
          'X-Tenant-ID': TENANT_ID
        }
      });
      console.log('AuthService: Logout API call completed');
    } catch (error) {
      console.error('AuthService: Logout API call failed:', error);
    } finally {
      // Always clear auth data regardless of API call success
      console.log('AuthService: Clearing auth data...');
      this.clearAuthData();
      console.log('AuthService: Auth data cleared');
    }
  }

  private clearAuthData(): void {
    console.log('AuthService: Clearing user data and refresh promise...');
    this.userData = null;
    this.refreshPromise = null;
    console.log('AuthService: Removing localStorage items...');
    localStorage.removeItem('authData');
    localStorage.removeItem('isAuthenticated');
    console.log('AuthService: Notifying auth change listeners...');
    this.notifyAuthChange();
    console.log('AuthService: Auth data clear completed');
  }

  addAuthChangeListener(listener: () => void): void {
    this.authChangeListeners.push(listener);
  }

  removeAuthChangeListener(listener: () => void): void {
    this.authChangeListeners = this.authChangeListeners.filter(l => l !== listener);
  }

  private notifyAuthChange(): void {
    this.authChangeListeners.forEach(listener => listener());
  }

  async refreshToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check rate limit
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      console.log('Refresh rate limited');
      return false;
    }

    this.lastRefreshAttempt = now;
    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    
    // Clear the promise after completion
    this.refreshPromise = null;
    
    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      console.log('Attempting to refresh token...');
      
      const role = this.getUserData()?.role;
      const refreshPath = role === 'Assistant' ? 'assistant/refresh-token' : 'ModeratorAuth/refresh-token';
      const refreshResponse = await fetch(`${API_BASE_URL}/${refreshPath}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Tenant-ID': TENANT_ID,
        },
      });
      
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        console.log('Refresh response:', refreshData);
        
        // Extract token from response structure
        if (refreshData?.success && refreshData?.data?.token) {
          const newToken = refreshData.data.token;
          const existingUser = this.getUserData();
          const incomingUser = refreshData.data.moderator || refreshData.data.assistant;
          const mergedUser = incomingUser
            ? {
                ...existingUser,
                ...incomingUser,
                role: existingUser?.role || incomingUser.role,
                privileges: incomingUser.privileges || existingUser?.privileges || [],
                name:
                  incomingUser.name ||
                  incomingUser.fullName ||
                  incomingUser.username ||
                  existingUser?.name,
              }
            : existingUser;
          
          console.log('New token obtained, updating auth data...');
          this.setAuthData(newToken, mergedUser);
          
          return true;
        } else {
          console.error('No token found in refresh response:', refreshData);
          return false;
        }
      } else if (refreshResponse.status === 400) {
        console.error('Refresh token is invalid or expired');
        this.clearAuthData();
        window.location.href = '/login';
        return false;
      } else {
        console.error('Refresh token request failed:', refreshResponse.status, refreshResponse.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error during token refresh:', error);
      return false;
    }
  }
}

export const authService = new AuthService();

export async function authFetch(input: RequestInfo, init: RequestInit = {}, retry = true): Promise<Response> {
  let token = authService.getToken();
  let headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add tenant ID header for multi-tenant support
  headers.set('X-Tenant-ID', TENANT_ID);
  
  // Always send cookies for refresh
  const fetchOptions: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };
  
  let response = await fetch(input, fetchOptions);
  
  if (response.status === 401 && retry) {
    console.log('Token expired, attempting to refresh...');
    
    const refreshSuccess = await authService.refreshToken();
    
    if (refreshSuccess) {
      // Retry original request with new token
      const newToken = authService.getToken();
      headers.set('Authorization', `Bearer ${newToken}`);
      headers.set('X-Tenant-ID', TENANT_ID);
      const retryOptions: RequestInit = {
        ...init,
        headers,
        credentials: 'include',
      };
      
      return fetch(input, retryOptions);
    } else {
      // If refresh fails, logout and redirect
      console.log('Token refresh failed, logging out...');
      await authService.logout();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
  }
  
  return response;
}

export const apiUrl = (path: string): string => {
  if (path.startsWith('/')) path = path.slice(1);
  return `${API_BASE_URL}/${path}`;
};

// Helper function to get full image URL
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) return null;
  
  // If the path already starts with http/https, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Construct the base URL for images by removing /api from the end
  const baseUrl = 'https://api.mromarelkholy.com';
  
  // Ensure the imagePath starts with /
  const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  return `${baseUrl}${path}`;
};

// Books API functions
export const booksApi = {
  // Get all books (Admin only)
  getBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books
  getActiveBooks: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get books by grade (Admin only)
  getBooksByGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get active books by grade
  getActiveBooksbyGrade: async (grade: string, pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`Book/active/grade/${grade}?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get book by ID
  getBookById: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`));
    return response.json();
  },

  // Create book without image
  createBook: async (bookData: any) => {
    const response = await authFetch(apiUrl('Book'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Create book with image
  createBookWithImage: async (formData: FormData) => {
    const response = await authFetch(apiUrl('Book/with-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },

  // Update book without image
  updateBook: async (id: string, bookData: any) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookData),
    });
    return response.json();
  },

  // Update book with image
  updateBookWithImage: async (id: string, formData: FormData) => {
    const response = await authFetch(apiUrl(`Book/${id}/with-image`), {
      method: 'PUT',
      body: formData,
    });
    return response.json();
  },

  // Delete book
  deleteBook: async (id: string) => {
    const response = await authFetch(apiUrl(`Book/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },

  // Upload book image
  uploadBookImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('imageFile', imageFile);
    
    const response = await authFetch(apiUrl('Book/upload-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

// Unit (standalone lecture) API helpers
export const unitApi = {
  uploadImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('imageFile', imageFile);

    const response = await authFetch(apiUrl('Unit/upload-image'), {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },
};

// Unit Codes API functions
export const unitCodesApi = {
  // Generate unit codes
  generateCodeUnit: async (unitId: number, count: number) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateCodeUnit?unitId=${unitId}&count=${count}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Generate general code
  generateGeneralCode: async (unitId: number, code: string) => {
    const response = await authFetch(apiUrl(`UnitCode/GenerateGeneralCode?unitId=${unitId}&generalCode=${encodeURIComponent(code)}`), {
      method: 'POST',
    });
    return response.json();
  },

  // Get all codes with optional teacher filtering
  getAllCodes: async (codeType: string, pageNumber: number = 1, pageSize: number = 10, teacherId?: string) => {
    const params = new URLSearchParams({
      codeType,
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    });
    
    const currentTeacherId = teacherId || getTeacherIdForAPI();
    if (currentTeacherId) {
      params.append('teacherId', currentTeacherId);
    }
    
    const response = await authFetch(apiUrl(`UnitCode/GetAllCodes?${params.toString()}`));
    return response.json();
  },

  // Get all general codes
  getAllGeneralCodes: async (page: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllGeneralCodes?page=${page}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all codes count
  getAllCodesCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllCodesCount'));
    return response.json();
  },

  // Get all student enter code
  getAllStudentEnterCode: async (pageNumber: number = 1, pageSize: number = 10) => {
    const response = await authFetch(apiUrl(`UnitCode/GetAllStudentEnterCode?pageNumber=${pageNumber}&pageSize=${pageSize}`));
    return response.json();
  },

  // Get all student enter code count
  getAllStudentEnterCodeCount: async () => {
    const response = await authFetch(apiUrl('UnitCode/GetAllStudentEnterCodeCount'));
    return response.text(); // Returns plain number
  },

  // Filter unit codes
  filterUnitCodes: async (filters: any) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value as string);
      }
    });
    
    const response = await authFetch(apiUrl(`UnitCode/FilterUnitCodes?${params.toString()}`));
    return response.json();
  },

  // Delete code
  deleteCode: async (id: string) => {
    const response = await authFetch(apiUrl(`UnitCode/DeleteCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

// Course tree API for getting units nested within courses
export const courseTreeApi = {
  getCourseTree: async (courseId: string) => {
    const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
      method: 'GET',
      headers: {
        'accept': '*/*'
      }
    });
    return response.json();
  }
};

// Helper function to get teacherId for filtering
const getTeacherIdForAPI = () => {
  const userData = authService.getUserData();
  return userData?.role === 'Teacher' ? userData.id : null;
};

// Course API functions
export const courseApi = {
  // Get courses with optional teacher filtering
  getCourses: async (pageNumber: number = 1, pageSize: number = 10, teacherId?: string) => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    });
    
    if (teacherId) {
      params.append('teacherId', teacherId);
    }
    
    const response = await authFetch(apiUrl(`Course?${params.toString()}`));
    return response.json();
  },

  // Add course with image upload
  addCourseWithImage: async (formData: FormData) => {
    // Add teacherId for Teacher role users
    const teacherId = getTeacherIdForAPI();
    if (teacherId && !formData.has('teacherId')) {
      formData.append('teacherId', teacherId);
    }
    
    const response = await authFetch(apiUrl('Course/addWithImage'), {
      method: 'POST',
      body: formData, // Don't set Content-Type header for FormData
    });
    return response.json();
  },
};

// Question Bank API functions
export const questionBankApi = {
  // Get questions with optional teacher filtering
  getQuestions: async (filters: any = {}) => {
    const params = new URLSearchParams();
    
    // Add teacher filtering for Teacher role users
    const teacherId = getTeacherIdForAPI();
    if (teacherId && !filters.teacherId) {
      filters.teacherId = teacherId;
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value as string);
      }
    });
    
    const response = await authFetch(apiUrl(`QuestionBank?${params.toString()}`));
    return response.json();
  },

  // Add questions with teacher context
  addQuestions: async (questionsData: any) => {
    // Ensure teacherId is set for Teacher role users
    const teacherId = getTeacherIdForAPI();
    if (teacherId && questionsData.questions) {
      questionsData.questions = questionsData.questions.map((q: any) => ({
        ...q,
        teacherId: q.teacherId || teacherId
      }));
    }
    
    const response = await authFetch(apiUrl('QuestionBank/bulk'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(questionsData),
    });
    return response.json();
  },
};

// Orders API functions
export const ordersApi = {
  // Get all orders (Moderator only)
  getAllOrders: async (
    pageNumber: number = 1,
    pageSize: number = 10,
    filters?: {
      orderId?: string;
      status?: string;
      type?: string;
      studentName?: string;
      studentPhoneNumber?: string;
      promoCode?: string;
      startDate?: string;
      endDate?: string;
      teacherId?: string;
    }
  ) => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    });

    if (filters?.orderId?.trim()) {
      params.append('orderId', filters.orderId.trim());
    }
    
    if (filters?.status) {
      params.append('status', filters.status);
    }

    if (filters?.type) {
      params.append('type', filters.type);
    }
    
    if (filters?.studentName) {
      params.append('studentName', filters.studentName);
    }
    
    if (filters?.studentPhoneNumber) {
      params.append('studentPhoneNumber', filters.studentPhoneNumber);
    }

    if (filters?.promoCode?.trim()) {
      params.append('promoCode', filters.promoCode.trim());
    }
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    if (filters?.teacherId) {
      params.append('teacherId', filters.teacherId);
    }
    
    const response = await authFetch(apiUrl(`Order/all?${params.toString()}`));
    return response.json();
  },

  // Get order by ID
  getOrderById: async (id: string) => {
    const response = await authFetch(apiUrl(`Order/${id}`));
    return response.json();
  },

  // Update order status
  updateOrderStatus: async (id: string, status: string) => {
    const response = await authFetch(apiUrl(`Order/${id}/status`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(status),
    });
    return response.json();
  },
};

export interface PromoCodeListParams {
  pageNumber?: number;
  pageSize?: number;
  /** Case-insensitive search on code and description */
  search?: string;
  courseId?: string;
  isActive?: boolean;
  /** 1 = MultiUse, 2 = SingleUse */
  type?: number;
  /** 1 = FixedAmount, 2 = Percentage */
  discountType?: number;
  /** true = expired only, false = valid (not expired) */
  isExpired?: boolean;
}

// PromoCodes API functions
export const promoCodesApi = {
  getAllPromoCodes: async (params: PromoCodeListParams | number = {}, legacyPageSize?: number) => {
    const p: PromoCodeListParams =
      typeof params === 'number'
        ? { pageNumber: params, pageSize: legacyPageSize ?? 10 }
        : params;
    const searchParams = new URLSearchParams();
    searchParams.set('pageNumber', String(p.pageNumber ?? 1));
    searchParams.set('pageSize', String(p.pageSize ?? 10));
    if (p.search?.trim()) searchParams.set('search', p.search.trim());
    if (p.courseId) searchParams.set('courseId', p.courseId);
    if (p.isActive !== undefined) searchParams.set('isActive', String(p.isActive));
    if (p.type !== undefined) searchParams.set('type', String(p.type));
    if (p.discountType !== undefined) searchParams.set('discountType', String(p.discountType));
    if (p.isExpired !== undefined) searchParams.set('isExpired', String(p.isExpired));

    const response = await authFetch(apiUrl(`PromoCode/all?${searchParams.toString()}`));
    return response.json();
  },

  // Get promocode by ID
  getPromoCodeById: async (id: string) => {
    const response = await authFetch(apiUrl(`PromoCode/${id}`));
    return response.json();
  },

  // Create promocode
  createPromoCode: async (promoCodeData: any) => {
    const response = await authFetch(apiUrl('PromoCode/create'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(promoCodeData),
    });
    
    const data = await response.json();
    
    // If response is not OK, include status in the error
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || `Server error: ${response.status} ${response.statusText}`,
        data: null
      };
    }
    
    return data;
  },

  // Create batch promocodes
  createPromoCodeBatch: async (batchData: any) => {
    const response = await authFetch(apiUrl('PromoCode/create-batch'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(batchData),
    });
    return response.json();
  },

  // Update promocode
  updatePromoCode: async (id: string, promoCodeData: any) => {
    const response = await authFetch(apiUrl(`PromoCode/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(promoCodeData),
    });
    return response.json();
  },

  // Delete promocode
  deletePromoCode: async (id: string) => {
    const response = await authFetch(apiUrl(`PromoCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

export const walletCodeCategoryApi = {
  getAll: async (isActive?: boolean) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.set('isActive', String(isActive));
    const query = params.toString();
    const response = await authFetch(
      apiUrl(`WalletCodeCategory/all${query ? `?${query}` : ''}`)
    );
    return response.json();
  },

  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCodeCategory/${id}`));
    return response.json();
  },

  create: async (dto: CreateWalletCodeCategoryDto) => {
    const response = await authFetch(apiUrl('WalletCodeCategory/create'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json();
  },

  update: async (id: string, dto: UpdateWalletCodeCategoryDto) => {
    const response = await authFetch(apiUrl(`WalletCodeCategory/update/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...dto, id }),
    });
    return response.json();
  },

  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCodeCategory/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

export const walletCodeApi = {
  getAll: async (params: WalletCodeListParams = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.set('pageNumber', String(params.pageNumber ?? 1));
    searchParams.set('pageSize', String(params.pageSize ?? 10));
    if (params.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params.isUsed !== undefined) searchParams.set('isUsed', String(params.isUsed));
    if (params.createdFrom) searchParams.set('createdFrom', params.createdFrom);
    if (params.createdTo) searchParams.set('createdTo', params.createdTo);
    if (params.searchTerm?.trim()) searchParams.set('searchTerm', params.searchTerm.trim());

    const response = await authFetch(apiUrl(`WalletCode/all?${searchParams.toString()}`));
    return response.json();
  },

  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCode/${id}`));
    return response.json();
  },

  create: async (dto: CreateWalletCodesDto) => {
    const response = await authFetch(apiUrl('WalletCode/create'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json();
  },

  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`WalletCode/${id}`), {
      method: 'DELETE',
    });
    return response.json();
  },
};

export const transactionApi = {
  getAll: async (params: TransactionListParams = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.set('pageNumber', String(params.pageNumber ?? 1));
    searchParams.set('pageSize', String(params.pageSize ?? 10));
    if (params.studentId?.trim()) searchParams.set('studentId', params.studentId.trim());
    if (params.teacherId) searchParams.set('teacherId', params.teacherId);
    if (params.type) searchParams.set('type', params.type);
    if (params.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params.toDate) searchParams.set('toDate', params.toDate);

    const response = await authFetch(apiUrl(`Transaction/all?${searchParams.toString()}`));
    return response.json();
  },

  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`Transaction/${id}`));
    return response.json();
  },

  getStudentHistory: async (studentId: string, pageNumber = 1, pageSize = 20) => {
    const searchParams = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    const response = await authFetch(
      apiUrl(`Transaction/student/${encodeURIComponent(studentId)}?${searchParams.toString()}`)
    );
    return response.json();
  },
};

export function unwrapAssistantCommunityPayload<T>(res: AssistantCommunityApiResponse): T {
  const { message, data } = res;
  if (message && typeof message === 'object') return message as T;
  if (data && typeof data === 'object') return data as T;
  throw new Error(String(message || data || 'Request failed'));
}

export function isAssistantCommunitySuccess(res: AssistantCommunityApiResponse): boolean {
  if (res.success) return true;
  const msg = String(res.message || res.data || '').toLowerCase();
  return msg.includes('success');
}

export const assistantApi = {
  register: async (dto: RegisterAssistantDto) => {
    const response = await authFetch(apiUrl('assistant/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  getAll: async (pageNumber = 1, pageSize = 10) => {
    const response = await authFetch(
      apiUrl(`assistant/get-all?pagenumber=${pageNumber}&pagesize=${pageSize}`)
    );
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch assistants'));
    }
    return unwrapAssistantCommunityPayload<AssistantListPayload>(json);
  },

  getById: async (id: string) => {
    const response = await authFetch(apiUrl(`assistant/${id}`));
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch assistant'));
    }
    return unwrapAssistantCommunityPayload<AssistantResponseDto>(json);
  },

  update: async (id: string, dto: UpdateAssistantDto) => {
    const response = await authFetch(apiUrl(`assistant/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  changePassword: async (id: string, dto: ChangeAssistantPasswordDto) => {
    const response = await authFetch(apiUrl(`assistant/changepassword/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`assistant/${id}`), { method: 'DELETE' });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  getCount: async () => {
    const response = await authFetch(apiUrl('assistant/count'));
    const json = (await response.json()) as AssistantCommunityApiResponse;
    return unwrapAssistantCommunityPayload<{ count: number }>(json);
  },

  getAvailablePrivileges: async () => {
    const response = await authFetch(apiUrl('assistant/privileges/available'));
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch privileges'));
    }
    return unwrapAssistantCommunityPayload<AvailablePrivilegesPayload>(json);
  },

  getPrivileges: async (id: string) => {
    const response = await authFetch(apiUrl(`assistant/${id}/privileges`));
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch assistant privileges'));
    }
    return unwrapAssistantCommunityPayload<AssistantPrivilegesPayload>(json);
  },

  updatePrivileges: async (id: string, dto: UpdateAssistantPrivilegesDto) => {
    const response = await authFetch(apiUrl(`assistant/${id}/privileges`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },
};

const buildCommentFilterQuery = (filter: CommentFilterDto = {}) => {
  const params = new URLSearchParams();
  if (filter.subjectId) params.append('subjectId', filter.subjectId);
  if (filter.courseId) params.append('courseId', filter.courseId);
  if (filter.unitId !== undefined) params.append('unitId', String(filter.unitId));
  if (filter.lessonId !== undefined) params.append('lessonId', String(filter.lessonId));
  if (filter.grade) params.append('grade', filter.grade);
  if (filter.status) params.append('status', filter.status);
  params.append('page', String(filter.page ?? 1));
  params.append('pageSize', String(filter.pageSize ?? 20));
  return params.toString();
};

export const communityApi = {
  getAssistantComments: async (filter: CommentFilterDto = {}) => {
    const response = await authFetch(
      apiUrl(`community/assistant/comments?${buildCommentFilterQuery(filter)}`)
    );
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch comments'));
    }
    return unwrapAssistantCommunityPayload<PaginatedCommentsResponseDto>(json);
  },

  changeCommentStatus: async (commentId: string, status: CommentStatus) => {
    const response = await authFetch(apiUrl(`community/assistant/comments/${commentId}/status`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  deleteComment: async (commentId: string) => {
    const response = await authFetch(apiUrl(`community/assistant/comments/${commentId}`), {
      method: 'DELETE',
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  createAssistantReply: async (commentId: string, text: string) => {
    const response = await authFetch(apiUrl(`community/assistant/comments/${commentId}/replies`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },

  getTeacherComments: async (filter: CommentFilterDto = {}) => {
    const response = await authFetch(
      apiUrl(`community/teacher/comments?${buildCommentFilterQuery(filter)}`)
    );
    const json = (await response.json()) as AssistantCommunityApiResponse;
    if (!response.ok && !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to fetch comments'));
    }
    return unwrapAssistantCommunityPayload<PaginatedCommentsResponseDto>(json);
  },

  createTeacherReply: async (commentId: string, text: string) => {
    const response = await authFetch(apiUrl(`community/teacher/comments/${commentId}/replies`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    return response.json() as Promise<AssistantCommunityApiResponse>;
  },
};

export const moderatorApi = {
  add: async (dto: RegisterModeratorDto) => {
    const response = await authFetch(apiUrl('ModeratorAuth/add-moderator'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: '*/*' },
      body: JSON.stringify(dto),
    });
    const json = (await response.json()) as ModeratorApiResponse<string>;
    if (!response.ok || !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to add moderator'));
    }
    return json;
  },

  getAll: async (pageNumber = 1, pageSize = 10) => {
    const response = await authFetch(
      apiUrl(`ModeratorAuth/get-all?pagenumber=${pageNumber}&pagesize=${pageSize}`)
    );
    const json = (await response.json()) as ModeratorApiResponse<ModeratorResponseDto[]>;
    if (!response.ok || !json.success) {
      throw new Error(String(json.message || 'Failed to fetch moderators'));
    }
    return json.data ?? [];
  },

  update: async (id: string, dto: UpdateModeratorDto) => {
    const response = await authFetch(apiUrl(`ModeratorAuth/info/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', accept: '*/*' },
      body: JSON.stringify(dto),
    });
    const json = (await response.json()) as ModeratorApiResponse<string>;
    if (!response.ok || !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to update moderator'));
    }
    return json;
  },

  delete: async (id: string) => {
    const response = await authFetch(apiUrl(`ModeratorAuth/Delete/${id}`), {
      method: 'DELETE',
    });
    const json = (await response.json()) as ModeratorApiResponse<string>;
    if (!response.ok || !json.success) {
      throw new Error(String(json.message || json.data || 'Failed to delete moderator'));
    }
    return json;
  },
};